import { computed, onUnmounted, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';

export const MAX_PINNED_CONVERSATIONS = 5;
export const PINNED_CONVERSATIONS_UPDATED = 'pinned-conversations:updated';

export const normalizePinnedConversationIds = ids =>
  [...new Set((Array.isArray(ids) ? ids : []).map(Number))]
    .filter(id => Number.isInteger(id) && id > 0)
    .slice(0, MAX_PINNED_CONVERSATIONS);

export const getPinnedConversationsStorageKey = (userId, accountId) => {
  const normalizedUserId = Number(userId);
  const normalizedAccountId = Number(accountId);

  if (
    !Number.isInteger(normalizedUserId) ||
    normalizedUserId <= 0 ||
    !Number.isInteger(normalizedAccountId) ||
    normalizedAccountId <= 0
  ) {
    return '';
  }

  return `${LOCAL_STORAGE_KEYS.PINNED_CONVERSATIONS}:${normalizedUserId}:${normalizedAccountId}`;
};

export const readPinnedConversationIds = storageKey => {
  if (!storageKey) return null;

  const storedIds = LocalStorage.get(storageKey);
  return Array.isArray(storedIds)
    ? normalizePinnedConversationIds(storedIds)
    : null;
};

export const persistPinnedConversationIds = (storageKey, ids) => {
  const normalizedIds = normalizePinnedConversationIds(ids);

  if (storageKey) {
    try {
      LocalStorage.set(storageKey, normalizedIds);
    } catch {
      // Keep the in-memory preference when localStorage is unavailable.
    }
  }

  return normalizedIds;
};

export const sortPinnedConversationsFirst = (conversations, pinnedIds) => {
  const pinPosition = new Map(
    normalizePinnedConversationIds(pinnedIds).map((id, index) => [id, index])
  );

  return conversations
    .map((conversation, index) => ({ conversation, index }))
    .sort((left, right) => {
      const leftPin = pinPosition.get(left.conversation.id);
      const rightPin = pinPosition.get(right.conversation.id);
      const leftIsPinned = leftPin !== undefined;
      const rightIsPinned = rightPin !== undefined;

      if (leftIsPinned && rightIsPinned) return leftPin - rightPin;
      if (leftIsPinned) return -1;
      if (rightIsPinned) return 1;
      return left.index - right.index;
    })
    .map(({ conversation }) => conversation);
};

export function usePinnedConversations() {
  const accountId = useMapGetter('getCurrentAccountId');
  const currentUserId = useMapGetter('getCurrentUserID');
  const { uiSettings, updateUISettings } = useUISettings();
  const localPinnedConversationIds = ref(null);

  const storageKey = computed(() =>
    getPinnedConversationsStorageKey(currentUserId.value, accountId.value)
  );

  const pinnedConversationsByAccount = computed(() => {
    const settings = uiSettings.value.pinned_conversations;
    return settings && typeof settings === 'object' && !Array.isArray(settings)
      ? settings
      : {};
  });

  const serverPinnedConversationIds = computed(() =>
    normalizePinnedConversationIds(
      pinnedConversationsByAccount.value[String(accountId.value)]
    )
  );

  watch(
    storageKey,
    key => {
      localPinnedConversationIds.value = readPinnedConversationIds(key);
    },
    { immediate: true, flush: 'sync' }
  );

  watch(
    [storageKey, serverPinnedConversationIds],
    ([key, serverIds]) => {
      if (
        !key ||
        localPinnedConversationIds.value !== null ||
        serverIds.length === 0
      ) {
        return;
      }

      localPinnedConversationIds.value = persistPinnedConversationIds(
        key,
        serverIds
      );
    },
    { immediate: true, flush: 'sync' }
  );

  const pinnedConversationIds = computed(
    () => localPinnedConversationIds.value ?? serverPinnedConversationIds.value
  );

  const syncPinnedConversationIds = ({ key, ids }) => {
    if (key !== storageKey.value) return;

    localPinnedConversationIds.value = normalizePinnedConversationIds(ids);
  };

  emitter.on(PINNED_CONVERSATIONS_UPDATED, syncPinnedConversationIds);
  onUnmounted(() => {
    emitter.off(PINNED_CONVERSATIONS_UPDATED, syncPinnedConversationIds);
  });

  const isConversationPinned = conversationId =>
    pinnedConversationIds.value.includes(Number(conversationId));

  const togglePinnedConversation = conversationId => {
    const id = Number(conversationId);
    const currentIds = pinnedConversationIds.value;
    const isPinned = currentIds.includes(id);

    if (!isPinned && currentIds.length >= MAX_PINNED_CONVERSATIONS) {
      return { limitReached: true, isPinned: false };
    }

    const nextIds = persistPinnedConversationIds(
      storageKey.value,
      isPinned
        ? currentIds.filter(currentId => currentId !== id)
        : [id, ...currentIds]
    );

    localPinnedConversationIds.value = nextIds;
    emitter.emit(PINNED_CONVERSATIONS_UPDATED, {
      key: storageKey.value,
      ids: nextIds,
    });

    updateUISettings({
      pinned_conversations: {
        ...pinnedConversationsByAccount.value,
        [String(accountId.value)]: nextIds,
      },
    });

    return { limitReached: false, isPinned: !isPinned };
  };

  return {
    pinnedConversationIds,
    isConversationPinned,
    togglePinnedConversation,
  };
}
